import { useLatestRef } from '@floating-ui/react/utils';
import { isEqual } from 'lodash-es';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';

import type { ListboxSelection, Option } from './types';

import { createValueLookup, isMultiSelect } from './utils';

/** A callback with a stable identity that always runs the latest `callback`. */
export function useStableCallback<A extends unknown[], R>(
  callback: (...args: A) => R,
) {
  const latestRef = useLatestRef(callback);
  return useCallback((...args: A) => latestRef.current(...args), [latestRef]);
}

type UseListboxStateOptions<V> = {
  /** Options in render order; a position here is a navigation index. */
  options: Option<V>[];
  selection: ListboxSelection<V>;
  /** Rows ahead of the options, so `selectedIndex` is a flat navigation index. */
  leadingRowCount?: number;
};

/**
 * State every listbox shell shares: the active row, keyboard modality, the row
 * elements Floating UI navigates, the selected-value lookup, and one stable
 * `onSelectOption`. Spread `listNavigation` into `useListNavigation`, adding
 * `disabledIndices` and `enabled`. Rows report hover through `onHoverIndex`;
 * `markKeyboardInput` / `markPointerInput` belong on the reference's and the
 * listbox's key down and the listbox's pointer move.
 */
export function useListboxState<V>({
  options,
  selection,
  leadingRowCount = 0,
}: UseListboxStateOptions<V>) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isKeyboardModality, setIsKeyboardModality] = useState(false);
  const [hasPointerLeft, setHasPointerLeft] = useState(false);
  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const isPointerModalityRef = useRef(true);

  const markKeyboardInput = useCallback(() => {
    isPointerModalityRef.current = false;
  }, []);
  const markPointerInput = useCallback(() => {
    isPointerModalityRef.current = true;
  }, []);

  const onHoverIndex = useStableCallback((index: number | null) => {
    // While keyboard-navigating, hover is visual-only so the focus ring /
    // aria-activedescendant stay on the keyboard-active option.
    if (isKeyboardModality) return;
    if (index === null && !isPointerModalityRef.current) return;
    setActiveIndex(index);
    if (index === null) setHasPointerLeft(true);
  });

  const resetNavigation = useCallback(() => {
    setActiveIndex(null);
    setIsKeyboardModality(false);
    setHasPointerLeft(false);
  }, []);

  const selectedValues = isMultiSelect(selection) ? selection.value : null;
  const singleValue = isMultiSelect(selection) ? null : selection.value;

  const isOptionSelected = useMemo(
    () =>
      createValueLookup(
        selectedValues ?? (singleValue == null ? [] : [singleValue]),
      ),
    [selectedValues, singleValue],
  );

  const selectedIndex = useMemo(() => {
    if (singleValue == null) return null;
    const index = options.findIndex((option) =>
      isEqual(option.value, singleValue),
    );
    return index === -1 ? null : index + leadingRowCount;
  }, [leadingRowCount, options, singleValue]);

  const onSelectOption = useStableCallback((option: Option<V>) => {
    if (option.disabled) return false;

    setIsKeyboardModality(false);
    const index = options.findIndex(({ id }) => id === option.id);
    if (index !== -1) setActiveIndex(index + leadingRowCount);

    if (isMultiSelect(selection)) {
      selection.onChange(
        isOptionSelected(option.value)
          ? selection.value.filter((value) => !isEqual(value, option.value))
          : [...selection.value, option.value],
      );
      return true;
    }

    selection.onChange(
      isEqual(selection.value, option.value) ? null : option.value,
    );
    return true;
  });

  return {
    activeIndex,
    elementsRef,
    isKeyboardModality,
    isOptionSelected,
    markKeyboardInput,
    markPointerInput,
    listNavigation: {
      activeIndex,
      focusItemOnHover: false,
      listRef: elementsRef,
      loop: true,
      selectedIndex: hasPointerLeft ? null : selectedIndex,
      virtual: true,
      onNavigate: setActiveIndex,
    },
    resetNavigation,
    selectedIndex,
    setActiveIndex,
    setIsKeyboardModality,
    onHoverIndex,
    onSelectOption,
  };
}

/** Ref callback that registers a row's element at its navigation index. */
export function useListboxItemRef(
  elementsRef: MutableRefObject<Array<HTMLElement | null>>,
  index: number,
) {
  return useCallback(
    (node: HTMLElement | null) => {
      elementsRef.current[index] = node;
    },
    [elementsRef, index],
  );
}
